import type { DiscoverFeedItem } from "@/domain/gems-game/domain/lib/Discover";
import {
  type Action,
  type BaseAnalyticsExtras,
  type ControlType,
  type InteractionType,
  TrackObject,
  useAnalyticsTracker,
} from "./AnalyticsProvider";

export enum DiscoverScreen {
  DISCOVER_FEED = "DiscoverFeed",
  DISCOVER_ONBOARDING = "Onboarding",
  DISCOVER_CONTENT = "DiscoverContent",
  PRIZE_UPFRONT = "PrizeUpfront",
  HEADER = "Header",
  REFERRAL_TEMPLATE = "ReferralTemplate",
  LOGIN = "Login",
  NEW_USER_VARIANT = "NewUserVariant",
  RETURNING_USER_VARIANT = "ReturningUserVariant",
  BONUS_GEMS_TEMPLATE = "BonusGemsTemplate",
  CTA_TEMPLATE = "CTATemplate",
  QUIZ_TEMPLATE = "QuizTemplate",
  CLAIM = "Claim",
  CONNECT_TO_CLAIM = "ConnectToClaim",
  PIN_TO_HOME_TEMPLATE = "PinToHomeTemplate",
  REWARD_POPUP = "RewardPopup",
  GAME_DETAILS = "GameDetails",
  SOCIAL_CONNECTION_TEMPLATE = "SocialConnectionTemplate",
  VIDEO_TEMPLATE = "VideoTemplate",
  FEED = "FEED",
  AB_TRANSITION_MODAL = "ABTransitionModal",
  QUEST_DIFFICULTY_SELECTION = "QuestDifficultySelection",
}

type DiscoverFeedItemType = NonNullable<DiscoverFeedItem>["type"];

// Base properties for any Discover Feed event
interface BaseDiscoverExtras extends BaseAnalyticsExtras {
  referrer?: string | null;
  externalAnonId?: string | null;
  onboardingExperience?: string;
  screen: DiscoverScreen;
}

interface BaseDiscoverContentExtras extends BaseDiscoverExtras {
  contentId: string;
  contentType: DiscoverFeedItemType;
  questId: string;
  gameId?: string;
  gameName?: string;
  feedIndex: number;
  priority: number;
  createdAt?: string;
  categories: string[];
  tags: string[];
}

// Event types discriminated by action
export type DiscoverFeedEvent =
  | {
      action: Action.VIEWED;
      extras: BaseDiscoverContentExtras & {
        completed?: boolean;
      };
    }
  | {
      action: Action.PRESSED;
      extras: BaseDiscoverContentExtras & {
        /** UI element pressed ("Option", "ChessBoard", etc.) */
        control: string;
        /** Label of UI element ("Add to Home", "Proceed", etc.) */
        controlLabel: string;
        controlType: ControlType;
        completed?: boolean;
        interactionType: InteractionType;
      } & (
          | {
              contentType: "quiz";
              optionIndex: number;
              correct: boolean;
              totalOptions: number;
            }
          | {
              contentType: Omit<DiscoverFeedItemType, "quiz">;
            }
          | {
              path: string;
              page_path: string;
            }
        );
    }
  | {
      action: Action.WATCHED;
      extras: BaseDiscoverContentExtras & {
        contentType: "video";
        videoLength: number;
        watchDuration: number;
        watchPercentage: number;
        interactionType: InteractionType;
      };
    }
  | {
      action: Action.EARNED;
      extras: BaseDiscoverContentExtras & {
        rewardAmount: number;
        /** What action led to earning ("quest", "daily", etc.) */
        completionSource: string;
        interactionType: InteractionType;
      };
    }
  | {
      action: Action.LANDED;
      extras: BaseDiscoverExtras;
    }
  | {
      action: Action.STARTED;
      extras: BaseDiscoverExtras;
    }
  | {
      action: Action.SKIPPED;
      extras: BaseDiscoverExtras;
    }
  | {
      action: Action.CONNECT;
      extras: BaseDiscoverExtras;
    }
  | {
      action: Action.COMPLETED;
      extras: BaseDiscoverExtras;
    }
  | {
      action: Action.PRESSED;
      extras: BaseDiscoverExtras;
    }
  | {
      action: Action.OPENED;
      extras: BaseDiscoverExtras;
    }
  | {
      action: Action.SUCCEEDED;
      extras: BaseDiscoverExtras;
    };

export const useDiscoverAnalytics = () =>
  useAnalyticsTracker<DiscoverFeedEvent>(TrackObject.FEED);
